import { ApiService } from '$lib/services/api';
import { Analytics } from '$lib/services/analytics/client';
import {
	PAYMENT_SDK_STATES,
	Sentry,
	markPayPalButtonInteractive,
	notePaypalSdkState,
} from '$lib/services/sentry';
import { CURRENCY_CODE } from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { ProductExperienceService } from '$lib/services/product-experience';
import { EVENT_TYPES } from '$events';
import {
	PAYPAL_ERROR_TYPES,
	PaypalService as BasePaypalService,
	type ApiService as PaypalApiService,
	type PaypalErrorEvent,
	type PaypalHooks,
} from '@mkt-client/payment-providers/paypal';

const paypalApiService: PaypalApiService = {
	async paymentPreProcess(
		aoflProductUuid: string,
		currencyCode: string,
		paymentProvider: 'paypal',
		paymentProviderInfo: object
	): Promise<string> {
		const response = await ApiService.paymentPreProcess({
			aofl_product_uuid: aoflProductUuid,
			payment_provider: paymentProvider,
			currency_code: currencyCode,
			payment_provider_info: paymentProviderInfo,
		});

		return response.payment_provider_info.client_token;
	},
};

/**
 * Error hook from paypal service
 * @param {import('@mkt-client/payment-providers/paypal').PaypalErrorEvent} event - Error event
 */
function handlePaypalError(event: PaypalErrorEvent): void {
	switch (event.type) {
		case PAYPAL_ERROR_TYPES.WINDOW_UNAVAILABLE:
			Sentry.captureException(event.error, {
				extra: {
					action: 'Error: PayPal window unavailable',
				},
			});
			break;
		case PAYPAL_ERROR_TYPES.APPROVE:
			Sentry.captureMessage('PayPal onApprove handler failed', {
				level: 'fatal',
				extra: {
					action: 'Error: PayPal onApprove',
					errorMessage: event.errorMessage,
					errorName: event.errorName,
					errorStack: event.errorStack,
					hasPayPalCheckoutInstance: event.hasPayPalCheckoutInstance,
					hasTokenizePayment: event.hasTokenizePayment,
					onApproveData: event.onApproveData,
					page_url: window.location.pathname,
				},
			});
			break;
		case PAYPAL_ERROR_TYPES.BUTTON:
			Sentry.captureException(event.error, {
				extra: {
					action: 'Error: PayPal OnError Button',
				},
			});
			break;
		case PAYPAL_ERROR_TYPES.RENDER:
			Sentry.captureException(event.error, {
				extra: {
					action: 'Error: PayPal Render Button',
				},
			});
			break;
		case PAYPAL_ERROR_TYPES.RENDER_BEFORE_READY:
			Sentry.captureException('Error: PayPal SDK render button called before initialization');
			break;
		case PAYPAL_ERROR_TYPES.SDK_INIT:
			Sentry.captureException(event.error, {
				extra: {
					action: 'Error: PayPal SDK missing after initialization',
				},
			});
			break;
	}
}

/**
 * Create PayPal hooks for the PayPal service for sentry/analytics.
 * @returns {PaypalHooks}
 */
function createPaypalHooks(): PaypalHooks {
	return {
		onButtonInitiated() {
			markPayPalButtonInteractive();
		},
		onCheckoutInitiated() {
			void Analytics.log(EVENT_TYPES.CLIENT_PAYMENT_FLOW_STATE, {
				provider: 'paypal',
				stage: 'checkout_initiated',
				provider_details: { page_url: window.location.pathname },
			});
		},
		onError(event) {
			handlePaypalError(event);
		},
		onPaymentApproved() {
			void Analytics.log(EVENT_TYPES.CLIENT_PAYMENT_FLOW_STATE, {
				provider: 'paypal',
				stage: 'authorized',
				outcome: 'success',
				provider_details: { page_url: window.location.pathname },
			});
		},
		onPaymentCancelled() {
			void Analytics.log(EVENT_TYPES.CLIENT_PAYMENT_FLOW_STATE, {
				provider: 'paypal',
				stage: 'cancelled',
				provider_details: { page_url: window.location.pathname },
			});
		},
	};
}

const paypalService = new BasePaypalService(paypalApiService, createPaypalHooks(), {
	getDeviceId,
	getAoflProductUuid: () => ProductExperienceService.AOFL_PRODUCT_UUID,
});

/**
 * Returns the session-scoped service shared by warmup and payment routes.
 * Records whether the route found an already-loaded PayPal SDK.
 * @returns {BasePaypalService}
 */
export function getPaypalService(): BasePaypalService {
	notePaypalSdkState(isPaypalSdkLoaded() ? PAYMENT_SDK_STATES.WARM : PAYMENT_SDK_STATES.COLD);
	return paypalService;
}

/**
 * Starts session-level SDK warmup without recording route warm/cold state.
 * @returns {Promise<void>}
 */
export function warmPaypalSdk(): Promise<void> {
	return paypalService.initializeSDK(CURRENCY_CODE.US_DOLLAR);
}

/**
 * Whether Braintree has already defined the PayPal SDK global on this page.
 * @returns {boolean}
 */
function isPaypalSdkLoaded(): boolean {
	if (typeof window === 'undefined') return false;
	return (window as unknown as { paypal?: unknown }).paypal != null;
}
